import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AUTH, ADMIN, CUSTOMER } from "@/constants/testIds";
import { PackageSearch, Truck, LogOut, LayoutDashboard, User, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkCls = (to) =>
    cn(
      "flex items-center gap-1.5 text-sm font-medium transition-colors",
      pathname === to ? "text-orange-400" : "text-slate-300 hover:text-white"
    );

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-600">
            <Truck className="h-5 w-5 text-white" />
          </div>
          <span className="text-lg font-black tracking-tight text-white">GO DELIVER</span>
        </Link>

        <nav className="flex items-center gap-5">
          <Link to="/" className={linkCls("/")} data-testid="nav-track-link">
            <PackageSearch className="h-4 w-4" /> <span className="hidden sm:inline">Track</span>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-company-menu"
              className="flex items-center gap-1 text-sm font-medium text-slate-300 outline-none transition-colors hover:text-white"
            >
              <span className="hidden sm:inline">Company</span>
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-white/10 bg-slate-900 text-slate-200">
              <DropdownMenuItem asChild><Link to="/about" data-testid="nav-about-link">About Go Deliver</Link></DropdownMenuItem>
              <DropdownMenuItem asChild><Link to="/portfolio" data-testid="nav-portfolio-link">Our Portfolio</Link></DropdownMenuItem>
              <DropdownMenuItem asChild><Link to="/info/logistics">Logistics</Link></DropdownMenuItem>
              <DropdownMenuItem asChild><Link to="/info/careers">Careers</Link></DropdownMenuItem>
              <DropdownMenuItem asChild><Link to="/info/developer-portal">Developer Portal</Link></DropdownMenuItem>
              <DropdownMenuItem asChild><Link to="/contact" data-testid="nav-contact-link">Contact Us</Link></DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user && user.role === "admin" && (
            <Link to="/admin" data-testid={ADMIN.navLink} className={linkCls("/admin")}>
              <LayoutDashboard className="h-4 w-4" /> <span className="hidden sm:inline">Admin Console</span>
            </Link>
          )}

          {user && user.role === "customer" && (
            <Link to="/my-shipments" data-testid={CUSTOMER.navLink} className={linkCls("/my-shipments")}>
              <User className="h-4 w-4" /> <span className="hidden sm:inline">My Shipments</span>
            </Link>
          )}

          {user ? (
            <div className="flex items-center gap-3">
              <span className="hidden text-xs font-mono text-slate-400 md:inline">{user.email}</span>
              <Button
                data-testid={AUTH.logoutButton}
                onClick={doLogout}
                variant="outline"
                size="sm"
                className="border-white/15 bg-transparent text-slate-200 hover:bg-white/10"
              >
                <LogOut className="mr-1 h-4 w-4" /> Logout
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => navigate("/login")}
              size="sm"
              className="bg-orange-600 text-white hover:bg-orange-700"
              data-testid="nav-login-button"
            >
              Sign In
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
};
